import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { getPostMeta, getPostBody, blogPosts } from './blog/posts';
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const meta = slug ? getPostMeta(slug) : undefined;
  const relatedPosts = blogPosts.filter(post => post.slug !== slug).slice(0, 2);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (meta) {
      document.title = `${meta.title} | IntelliEats Blog`;
      let description = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
      if (!description) {
        description = document.createElement('meta');
        description.name = 'description';
        document.head.appendChild(description);
      }
      description.content = meta.excerpt;
    }
    // Google AdSense - inject into <head>
    if (!document.querySelector('script[src*="adsbygoogle"]')) {
      const adsScript = document.createElement('script');
      adsScript.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3041826185319356';
      adsScript.async = true;
      adsScript.setAttribute('crossorigin', 'anonymous');
      document.head.appendChild(adsScript);
    }
  }, [meta]);
  if (!meta) {
    return <Navigate to="/blog" replace data-id="element-1099" data-mp="3kygxwn0259m0" />;
  }
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-1100" data-mp="3kzs2gp1bule6">
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1101" data-mp="3txkixwisbz35">
        {/* Header */}
        <header className="mb-12 text-center" data-id="element-1102" data-mp="2k7p41uyqitv">
          <Link to="/blog" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-1103" data-mp="2m10fugitr87">
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1104" data-mp="367gh9up5q9es" />
            Back to Blog
          </Link>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-6" data-id="element-1105" data-mp="dd9h4e5yzzn9">
            {meta.title}
          </h1>
          <div className="flex items-center justify-center text-sm text-slate-500 space-x-4" data-id="element-1106" data-mp="m6uvhwe83ew5">
            <span data-id="element-1107" data-mp="i59cdtb3i4di">By IntelliEats Team</span>
            <span data-id="element-1108" data-mp="i6c76cbjgqyp">•</span>
            <span className="flex items-center" data-id="element-1109" data-mp="i62m935cp9zv">
              <Clock className="w-4 h-4 mr-1" data-id="element-1110" data-mp="3qkkq0so9e3do" />
              {meta.readTime}
            </span>
          </div>
        </header>

        {/* Body */}
        {getPostBody(meta.slug)}

        {/* Medical Disclaimer */}
        <div className="mt-16 bg-slate-50 border border-slate-200 rounded-2xl p-6" data-id="element-1111" data-mp="3hfw06e6qxtv">
          <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2" data-id="element-1112" data-mp="3umen6qz8wp93">
            Medical Disclaimer
          </h2>
          <p className="text-sm text-slate-500 leading-relaxed m-0" data-id="element-1113" data-mp="1etyyjvtr9j5a">
            The information in this article is provided for educational purposes
            only and is not intended as medical, nutritional, or dietary advice.
            Ingredients approved by regulatory authorities are considered safe
            when used within established limits. Always consult a qualified
            healthcare professional or registered dietitian for guidance
            tailored to your personal health needs. See our{' '}
            <Link to="/editorial-policy" className="text-brand-600 hover:text-brand-700 font-medium" data-id="element-1114" data-mp="1dg7kplqxhpsh">
              Editorial Policy
            </Link>{' '}
            to learn how we research and review our content.
          </p>
        </div>

        {/* Related Articles */}
        {relatedPosts.length > 0 && <div className="mt-16 pt-12 border-t border-slate-200" data-id="element-1115" data-mp="1wbxo9deieu8h">
            <h2 className="text-2xl font-black text-slate-900 mb-8 text-center" data-id="element-1116" data-mp="2k2b8wiurlqt1">
              Keep Reading
            </h2>
            <div className="grid sm:grid-cols-2 gap-6" data-id="element-1117" data-mp="2oul2c8v39902">
              {relatedPosts.map(post => <Link key={post.slug} to={`/blog/${post.slug}`} className="group block bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden text-left" data-id="element-1118" data-mp="108i6abpwuzdh">
                  <div className="h-40 overflow-hidden" data-id="element-1119" data-mp="m9igv1ukqgg9">
                    <img src={post.image} alt={post.imageAlt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1120" data-mp="1jw7phujs7rb3" />
                  </div>
                  <div className="p-6" data-id="element-1121" data-mp="1a3to52wm8mpg">
                    <span className="px-2.5 py-1 rounded-full bg-brand-50 text-brand-700 text-xs font-bold tracking-wider uppercase" data-id="element-1122" data-mp="157lbbw5w55lg">
                      {post.category}
                    </span>
                    <h3 className="text-lg font-bold text-slate-900 mt-3 mb-3 leading-snug group-hover:text-brand-700 transition-colors line-clamp-2" data-id="element-1123" data-mp="1xv7dr13du46q">
                      {post.title}
                    </h3>
                    <span className="inline-flex items-center text-sm font-semibold text-brand-600 group-hover:text-brand-700" data-id="element-1124" data-mp="2a6rntzegdspe">
                      Read Article
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" data-id="element-1125" data-mp="2k3w97x0h5k55" />
                    </span>
                  </div>
                </Link>)}
            </div>
          </div>}
      </article>
    </motion.div>;
}